import CovidDateRangeSelection from '@root/joinroot.com/src/components/covid-driving/global-controls/date-range-selection';
import CovidMetricControls from '@root/joinroot.com/src/components/covid-driving/global-controls/metric-controls';
import DateDropdownButton from '@root/joinroot.com/src/components/covid-driving/global-controls/date-dropdown-button';
import PropTypes from '@root/vendor/prop-types';
import React, { useCallback, useEffect, useRef, useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import useScrollPosition from '@root/core/src/hooks/use-scroll-position';
import { StyleSheet } from '@root/core/src/utils/styles';

export default function CovidDrivingGlobalControls({
  earliestDate,
  endDate,
  latestDate,
  metric,
  setEndDate,
  setMetric,
  setStartDate,
  startDate,
  trackClick,
}) {
  const globalControlsRef = useRef();
  const [initialTopPosition, setInitialTopPosition] = useState();
  const [shouldSlideControlsDown, setShouldSlideControlsDown] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  useEffect(() => setInitialTopPosition(globalControlsRef.current.getBoundingClientRect().top), []);

  useScrollPosition(useCallback(({ previousPosition, currentPosition }) => {
    // This coordinates with the main nav.
    const hasScrolledToControls = currentPosition.y <= -initialTopPosition;
    const shouldSlide = currentPosition.y > previousPosition.y && hasScrolledToControls;
    setShouldSlideControlsDown(shouldSlide);
  }, [initialTopPosition]));

  const handleDateDropdown = () => {
    setIsDateDropdownOpen(!isDateDropdownOpen);
  };

  const handleClickOutside = (e) => {
    if (globalControlsRef.current.contains(e.target)) { return; }
    setIsDateDropdownOpen(false);
  };

  useEffect(() => {
    if (isDateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDateDropdownOpen]);

  return (
    <div
      css={[styles.globalControls, shouldSlideControlsDown && styles.controlsDown]}
      ref={globalControlsRef}
    >
      <CovidMetricControls
        metric={metric}
        setMetric={setMetric}
        trackClick={trackClick}
      />
      <DateDropdownButton handleDateDropdown={handleDateDropdown} />
      <CovidDateRangeSelection
        earliestDate={earliestDate}
        endDate={endDate}
        isDateDropdownOpen={isDateDropdownOpen}
        latestDate={latestDate}
        setEndDate={setEndDate}
        setStartDate={setStartDate}
        startDate={startDate}
        trackClick={trackClick}
      />
    </div>
  );
}

CovidDrivingGlobalControls.propTypes = {
  earliestDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  latestDate: PropTypes.instanceOf(Date),
  metric: PropTypes.string,
  setEndDate: PropTypes.func,
  setMetric: PropTypes.func,
  setStartDate: PropTypes.func,
  startDate: PropTypes.instanceOf(Date),
  trackClick: PropTypes.func,
};

const mainNavEasing = 'cubic-bezier(0.37, 0, 0.28, 1)';

const styles = StyleSheet.create({
  globalControls: {
    position: 'sticky',
    zIndex: 2,
    top: 0,
    display: 'flex',
    justifyContent: 'center',
    margin: '0 auto',
    padding: 10,
    transform: 'translate3d(0,0,0)',
    transition: `transform 500ms ${mainNavEasing}`,
    ...Responsive.lessThanSm({
      width: '100%',
    }),
  },
  controlsDown: {
    transform: 'translate3d(0,50px,0)',
    ...Responsive.md({
      transform: 'translate3d(0,100px,0)',
    }),
    ...Responsive.lg({
      transform: 'translate3d(0,60px,0)',
    }),
  },
});
